import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Header from './Header';
import './DeviceAuth.css';

const DeviceAuth = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [userCode, setUserCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Auto-fill code from URL parameter
    const codeParam = searchParams.get('code');
    if (codeParam) {
      setUserCode(codeParam.toUpperCase());
    }
  }, [searchParams]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleAuthorize = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token || !user) {
        // Redirect to auth with return URL (use pathname + search, not full href)
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/auth?redirect=${returnUrl}`);
        return;
      }

      const response = await fetch('/api/oauth/device/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ user_code: userCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authorization failed');
      }

      setSuccess(true);
    } catch (err) {
      console.error('Authorization error:', err);
      setError(err.message || 'Failed to authorize device');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="device-auth-page">
        <Header />
        <main className="device-auth-main">
          <div className="device-auth-card">
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (success) {
    return (
      <div className="device-auth-page">
        <Header />
        <main className="device-auth-main">
          <div className="device-auth-card success">
            <div className="success-icon">✓</div>
            <h1>Authorization Successful!</h1>
            <p>You can now close this window and return to your application.</p>
            <p className="note">Your device has been authorized and will receive an access token shortly.</p>
          </div>
        </main>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!authLoading && !user) {
    return (
      <div className="device-auth-page">
        <Header />
        <main className="device-auth-main">
          <div className="device-auth-card">
            <h1>Authorize Device</h1>
            <p className="subtitle">
              Please log in to authorize this device.
            </p>

            {userCode && (
              <div className="code-display">
                <label>Authorization Code:</label>
                <div className="code-value">{userCode}</div>
              </div>
            )}

            <div className="help-text">
              <p style={{ textAlign: 'center', margin: '0 0 20px 0' }}>
                You need to be logged in to authorize a device. Please log in to continue.
              </p>
            </div>

            <button
              className="authorize-button"
              onClick={() => {
                const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
                navigate(`/auth?redirect=${returnUrl}`);
              }}
            >
              Log In to Continue
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="device-auth-page">
      <Header />
      <main className="device-auth-main">
        <div className="device-auth-card">
          <h1>Authorize Device</h1>
          <p className="subtitle">
            Authorize Rec-Chord to access your account from a device or application.
          </p>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {userCode && (
            <div className="code-display">
              <label>Authorization Code:</label>
              <div className="code-value">{userCode}</div>
            </div>
          )}

          {!userCode && (
            <div className="code-display">
              <label>Device Code:</label>
              <input
                type="text"
                value={userCode}
                onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                placeholder="Enter device code"
                maxLength={9}
                pattern="[A-Z0-9-]*"
              />
            </div>
          )}

          <button
            className="authorize-button"
            onClick={handleAuthorize}
            disabled={loading || !userCode || userCode.length < 8}
          >
            {loading ? 'Authorizing...' : 'Authorize Device'}
          </button>

          <div className="help-text">
            <p style={{ textAlign: 'center', margin: '0 0 12px 0' }}>
              By clicking "Authorize Device", you're granting access to your Rec-Chord account
              from the device or application that provided this code.
            </p>
            <p className="note" style={{ textAlign: 'center', margin: 0 }}>
              Make sure you recognize this code and only authorize devices you trust.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DeviceAuth;
